import React from 'react';

import { ApiError } from '../../../adapters/api-client/api';

export interface ErrorStateProps {
  /** What the user was trying to do, e.g. "cargar tu panel". */
  action?: string;
  /** The failure itself. An ApiError also carries the correlation id. */
  error: unknown;
  /** Shown as a retry button when provided. */
  onRetry?: () => void;
  /** Rendered instead of the default frame when the caller needs it inline. */
  compact?: boolean;
}

const box: React.CSSProperties = {
  border: '1px solid #f1aeae',
  background: '#fff5f5',
  borderRadius: 10,
  padding: 16,
};

const button: React.CSSProperties = {
  marginTop: 12,
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid #ced4da',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 14,
};

export function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return 'Algo salió mal.';
}

/** The correlation id, when the failure came from the API (S11.4). */
export function errorRequestId(error: unknown): string | undefined {
  return error instanceof ApiError ? error.requestId : undefined;
}

/**
 * S1.3 / S11.4 — one error state, so screens stop inventing their own.
 *
 * Three things a failed screen owes the person looking at it: what failed, a
 * way to try again, and — when the backend gave us one — the identifier that
 * lets support find the request in the logs. The last is the difference between
 * "it broke" and a case somebody can actually look up: the same id is on every
 * log line that request produced.
 */
export const ErrorState: React.FC<ErrorStateProps> = ({ action, error, onRetry, compact }) => {
  const requestId = errorRequestId(error);
  const message = errorMessage(error);

  return (
    <div style={compact ? { ...box, padding: 12 } : box} role="alert">
      <p style={{ margin: 0, color: '#c92a2a', fontWeight: 600 }}>
        {action ? `No pudimos ${action}.` : 'Algo salió mal.'}
      </p>
      <p style={{ margin: '6px 0 0', color: '#666', fontSize: 14 }}>{message}</p>
      {requestId && (
        <p style={{ margin: '8px 0 0', color: '#868e96', fontSize: 12 }}>
          Si escribes a soporte, incluye esta referencia:{' '}
          <code style={{ userSelect: 'all' }}>{requestId}</code>
        </p>
      )}
      {onRetry && (
        <button onClick={onRetry} style={button}>
          Reintentar
        </button>
      )}
    </div>
  );
};

export default ErrorState;
